import { Component, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NavbarComponent } from '../navbar-v2/navbar-v2.component';
import { CP_ICONS } from '../../../pages/v3/shared/icons';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY } from '../../../pages/v3/shared/ui';
import { SOLUTION_MENU } from '../../../pages/v3/shared/solutions-menu';
import { SOLUTION_PARAM, goToHomeSection } from '../../../pages/v3/shared/navigation';

const OPEN_DELAY_MS = 80;
const CLOSE_DELAY_MS = 160;

/**
 * v3 navbar. Inherits auth state, role gating, theme toggle, and the account
 * menu actions from the v2 navbar; adds the Credit Solutions menu.
 */
@Component({
  selector: 'app-navbar-v3',
  standalone: true,
  imports: [RouterModule, NgIcon],
  viewProviders: [provideIcons(CP_ICONS)],
  templateUrl: './navbar-v3.component.html',
})
export class NavbarV3Component extends NavbarComponent {
  private readonly nav = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private hoverTimer?: ReturnType<typeof setTimeout>;

  private readonly accountTrigger = viewChild<ElementRef<HTMLButtonElement>>('accountTrigger');
  private readonly solutionsTrigger = viewChild<ElementRef<HTMLButtonElement>>('solutionsTrigger');
  private readonly solutionsPanel = viewChild<ElementRef<HTMLElement>>('solutionsPanel');

  readonly btnPrimary = BTN_PRIMARY;
  readonly btnSecondary = BTN_SECONDARY;
  readonly btnPrimarySm = BTN_PRIMARY_SM;
  readonly navLink =
    'rounded-full px-3 py-1.5 text-[14px] text-cp-muted transition-colors hover:text-cp-ink ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-accent';
  readonly menuItem =
    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-cp-ink transition-colors hover:bg-cp-fill';
  readonly solutionGroups = SOLUTION_MENU;

  readonly solutionsOpen = signal(false);
  readonly mobileSolutionsOpen = signal(false);

  /** Logged-in primary destination; admins land on the advisor portal and get both in the menu. */
  get portal(): { label: string; path: string } | null {
    if (this.showAdvisorPortal) {
      return { label: 'Advisor Portal', path: '/advisor-portal' };
    }
    if (this.showLenderPortal) {
      return { label: 'Lender Portal', path: '/lender-portal' };
    }
    return null;
  }

  solutionHref(id: string): string {
    return `/?${SOLUTION_PARAM}=${encodeURIComponent(id)}#solutions`;
  }

  private get onHome(): boolean {
    return this.nav.url.split(/[?#]/)[0] === '/';
  }

  /** The logo links home; on the home page it scrolls back to the top instead. */
  onLogoClick(event: MouseEvent): void {
    this.closeAll();
    if (this.onHome && !this.hasModifier(event)) {
      event.preventDefault();
      const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  goToSection(sectionId: string): void {
    this.closeAll();
    void goToHomeSection(this.nav, sectionId);
  }

  /** Plain clicks navigate in-app (manual scroll); modified clicks keep the real href. */
  selectSolution(event: MouseEvent, id: string): void {
    if (this.hasModifier(event)) {
      return;
    }
    event.preventDefault();
    this.closeAll();
    void goToHomeSection(this.nav, 'solutions', { [SOLUTION_PARAM]: id });
  }

  signUp(): void {
    this.closeAll();
    this.nav.navigate(['/auth'], { queryParams: { mode: 'signup' } });
  }

  // --- Credit Solutions menu: hover intent, click, keyboard ---

  onSolutionsPointerEnter(): void {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.openSolutions(), OPEN_DELAY_MS);
  }

  onSolutionsPointerLeave(): void {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.solutionsOpen.set(false), CLOSE_DELAY_MS);
  }

  toggleSolutions(): void {
    clearTimeout(this.hoverTimer);
    if (this.solutionsOpen()) {
      this.solutionsOpen.set(false);
    } else {
      this.openSolutions();
    }
  }

  onSolutionsTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openSolutions();
      setTimeout(() => this.focusSolutionItem(0));
    }
  }

  onSolutionsPanelKeydown(event: KeyboardEvent): void {
    const items = this.solutionItems();
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.focusSolutionItem((index + step + items.length) % items.length);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.focusSolutionItem(event.key === 'Home' ? 0 : items.length - 1);
    }
  }

  /** Close when focus leaves both the trigger and the panel (e.g. Tab past the last item). */
  onSolutionsFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    const inside =
      !!next &&
      (!!this.solutionsPanel()?.nativeElement.contains(next) || !!this.solutionsTrigger()?.nativeElement.contains(next));
    if (!inside) {
      this.solutionsOpen.set(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.solutionsOpen.set(false);
      this.userMenuOpen.set(false);
    }
  }

  /** Escape closes whichever menu is open and returns focus to its trigger. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.solutionsOpen()) {
      this.solutionsOpen.set(false);
      this.solutionsTrigger()?.nativeElement.focus();
    }
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
      this.accountTrigger()?.nativeElement.focus();
    }
    this.mobileMenuOpen = false;
  }

  private openSolutions(): void {
    this.userMenuOpen.set(false);
    this.solutionsOpen.set(true);
  }

  private closeAll(): void {
    clearTimeout(this.hoverTimer);
    this.solutionsOpen.set(false);
    this.userMenuOpen.set(false);
    this.mobileMenuOpen = false;
    this.mobileSolutionsOpen.set(false);
  }

  private solutionItems(): HTMLElement[] {
    const panel = this.solutionsPanel()?.nativeElement;
    return panel ? Array.from(panel.querySelectorAll<HTMLElement>('[data-menu-item]')) : [];
  }

  private focusSolutionItem(index: number): void {
    this.solutionItems()[index]?.focus();
  }

  private hasModifier(event: MouseEvent): boolean {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }
}
